import { motion, type Variants } from 'framer-motion';
import { useCallback, useEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { Button } from '~/components/ui/Button';
import { useScrollListener } from '~/lib/hooks/useScrollListener';
import { classNames } from '~/utils/classNames';
import { cubicEasingFn } from '~/utils/easings';
import { resourcesAndSocialLinks } from '~/utils/homepage-navbar-links';
import { isTouchDevice } from '~/utils/isTouchDevice';
import { resourcesLinks } from '~/utils/resources-links';
import { socialLinks } from '~/utils/socialLinks';

const RESOURCES_DROPDOWN_ID = 'resources-dropdown';
const MENU_ITEM_NAME = 'Resources';
const HIDE_DELAY = 200;
const ANIMATION_DURATION = 0.2;
const MENU_POSITION_CLOSED = '44px';
const MENU_POSITION_OPEN = '54px';

const LINK_CLASSES = classNames(
  'group flex items-center justify-start p-2 text-sm rounded-md font-medium bg-transparent',
  'gap-2 hover:bg-bolt-ds-surface-highlight outline-none focus:bg-bolt-ds-surface-highlight',
);

const menuVariants = {
  closed: {
    opacity: 0,
    visibility: 'hidden',
    top: MENU_POSITION_CLOSED,
    transition: {
      duration: ANIMATION_DURATION,
      ease: cubicEasingFn,
    },
  },
  open: {
    opacity: 1,
    visibility: 'initial',
    top: MENU_POSITION_OPEN,
    transition: {
      duration: ANIMATION_DURATION,
      ease: cubicEasingFn,
    },
  },
} satisfies Variants;

interface ResourcesDropdownProps {
  className?: string;
  activeClass?: string;
}

export const ResourcesDropdown = ({ className, activeClass }: ResourcesDropdownProps) => {
  const timerRef = useRef<ReturnType<typeof setTimeout>>();
  const [open, setOpen] = useState(false);

  const handleLinkClick = useCallback(() => {
    setOpen(false);
  }, []);

  const showMenu = useCallback((e: ReactMouseEvent) => {
    e.stopPropagation();

    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    setOpen(true);
  }, []);

  const hideMenu = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }

    timerRef.current = setTimeout(() => {
      setOpen(false);
    }, HIDE_DELAY);
  }, []);

  const addClasses: Record<string, boolean> = {};

  if (activeClass) {
    addClasses[activeClass] = open;
  }

  /** This closes the dropdown when scrolling on touch devices, specifically on tablets wider than 1024px */
  useScrollListener(hideMenu, open && isTouchDevice);

  /** Close the dropdown when clicking outside of it on touch devices */
  useEffect(() => {
    if (!open || !isTouchDevice) {
      return undefined;
    }

    function handleClickOutside(event: MouseEvent) {
      if (!event.target || !(event.target instanceof HTMLElement)) {
        return;
      }

      if (!event.target.closest(`#${RESOURCES_DROPDOWN_ID}`)) {
        event.stopImmediatePropagation();
        setOpen(false);
      }
    }

    document.addEventListener('click', handleClickOutside, { capture: true });

    return () => {
      document.removeEventListener('click', handleClickOutside, { capture: true });
    };
  }, [open]);

  const mouseOverHandler = isTouchDevice ? undefined : showMenu;
  const onMouseLeaveHandler = isTouchDevice ? undefined : hideMenu;

  return (
    <>
      <Button
        className={classNames(className, addClasses)}
        onClick={showMenu}
        onMouseOver={mouseOverHandler}
        onMouseLeave={onMouseLeaveHandler}
        variant="link-gray"
        endIcon={`i-ph:caret-down transition-transform ${open ? '-rotate-180' : 'rotate-0'}`}
        size="md"
      >
        {MENU_ITEM_NAME}
      </Button>

      <motion.div
        id={RESOURCES_DROPDOWN_ID}
        onMouseOver={mouseOverHandler}
        onMouseLeave={onMouseLeaveHandler}
        initial="closed"
        animate={open ? 'open' : 'closed'}
        variants={menuVariants}
        className={classNames(
          'fixed left-50% -transform-translate-x-50% z-resources',
          'border border-bolt-ds-borderOutline rounded-xl bg-bolt-ds-surfaceOne',
          'shadow-[0_4px_6px_-2px_rgba(0,0,0,0.05),0_10px_15px_-3px_rgba(0,0,0,0.1)]',
          'before:content-[""] before:absolute before:w-full before:h-2 before:bottom-full before:left-0 before:bg-transparent',
        )}
      >
        <div className="grid grid-cols-[246px_1fr] p-6 gap-6 items-start">
          <section className="text-sm flex flex-col gap-2">
            <h3 className="uppercase pl-2 font-bold text-sm text-bolt-elements-textTertiary">{MENU_ITEM_NAME}</h3>

            {resourcesLinks.map(({ icon, name, href, openInNewTab }, i) => {
              const itemProps: { target?: '_blank' } = {};

              if (openInNewTab) {
                itemProps.target = '_blank';
              }

              return (
                <a key={i} {...itemProps} href={href} className={LINK_CLASSES} onClick={handleLinkClick}>
                  {icon && <span className={icon} />}
                  {name}
                  <span className="i-heroicons:arrow-up-right-solid text-bolt-elements-textSecondary opacity-0 group-hover:opacity-100 ml-auto" />
                </a>
              );
            })}
          </section>

          <section className="flex flex-col gap-2">
            <h3 className="uppercase pl-2 font-bold text-sm text-bolt-elements-textTertiary">Community</h3>
            <ul className="grid grid-cols-[188px_188px] gap-2">
              {socialLinks.map(({ icon, href, name }, index) => {
                return (
                  <li key={index}>
                    <a href={href} className={LINK_CLASSES} target="_blank" onClick={handleLinkClick}>
                      <span className={classNames('block size-4.5 shrink-0', icon)} />
                      {name}
                      <span className="i-heroicons:arrow-up-right-solid text-bolt-elements-textSecondary opacity-0 group-hover:opacity-100 ml-auto" />
                    </a>
                  </li>
                );
              })}
            </ul>
          </section>
        </div>
      </motion.div>
    </>
  );
};

const RESOURCES_DEFAULT_OPEN = false;

export const MobileResourcesDropdown = ({ className }: ResourcesDropdownProps) => {
  const [openResources, setOpenResources] = useState(RESOURCES_DEFAULT_OPEN);
  const resourcesLinksLength = resourcesLinks.length;

  const toggleResources = useCallback(() => {
    setOpenResources((v) => !v);
  }, []);

  const closeResources = useCallback(() => {
    setOpenResources(false);
  }, []);

  return (
    <>
      <Button
        className={classNames(className, 'h-auto justify-start')}
        endIcon={`i-ph:caret-down transition-transform ${openResources ? '-rotate-180' : 'rotate-0'}`}
        variant="link-gray"
        onClick={toggleResources}
      >
        Resources
      </Button>

      <div
        className={classNames(
          'transition-[opacity] duration-300 ease-in-out flex flex-col gap-2 will-change-[opacity]',
          openResources ? 'opacity-100' : 'opacity-0',
        )}
      >
        {openResources && (
          <>
            {resourcesAndSocialLinks.map(({ icon, name, href, openInNewTab }, i) => {
              const itemProps: { target?: '_blank' } = {};

              if (openInNewTab) {
                itemProps.target = '_blank';
              }

              return (
                <a
                  key={i}
                  {...itemProps}
                  href={href}
                  className={classNames(
                    'flex items-center gap-2 p-2 text-base',
                    `[&:nth-child(${resourcesLinksLength})]:mb-4`,
                  )}
                  onClick={closeResources}
                >
                  {icon && <span className={icon} />}
                  {name}
                </a>
              );
            })}
          </>
        )}
      </div>
    </>
  );
};
